import {
  DirectedTripleWithFilters,
  QueryBuilderSubquery,
  ViewpointBuilderFilters,
} from '@ardoq/api-types';
import { omit } from 'lodash';
import { isExclusionRule } from 'loadedState/excludeComponent/excludeComponentOperations';

type GetStartSetFilterArgs = {
  paths?: DirectedTripleWithFilters[][];
  filters?: ViewpointBuilderFilters;
};

/**
 * The filter on the start node, as an advanced-search subquery for choosing a context component.
 *
 * Exclusions are dropped. The start node's filter has two jobs: it constrains which components may
 * be the context, and - since a component excluded from a viewpoint has to be droppable at the start
 * node too, that being the only way to drop the context component itself - it also carries
 * `_id != X` rules. Those are the same filter but a different question, and this one answers only
 * the first.
 *
 * Leaving them in fed `_id != X` to `/api/search` through both callers: the viewpoint navigator's
 * context switcher, where the excluded component silently vanished from the components you could
 * switch to, and `initViewpointBuilder` on `openViewpoint`, where **Select all** nested it into
 * `componentSelection.startQuery`. That second one is the reason this matters rather than being
 * cosmetic: nothing that clears an exclusion looks at `componentSelection`, so once it was in
 * `startQuery` the count read zero while the component stayed hidden, with nothing left in the
 * product that knew about it.
 */
export const getStartSetFilter = ({
  paths,
  filters,
}: GetStartSetFilterArgs): QueryBuilderSubquery | null => {
  if (!(paths && paths[0] && paths[0][0] && filters)) {
    return null;
  }
  const { direction, sourceFilter, targetFilter } = paths[0][0];
  const startSetFilterId =
    direction === 'outgoing' ? sourceFilter : targetFilter;
  if (!startSetFilterId) return null;

  const filter = filters[startSetFilterId];
  if (!filter) return null;

  const rules = filter.rules.filter(rule => !isExclusionRule(rule));
  /* Nothing left means there was no start-set filter before the exclusion was written, which is the
     common case: `addExclusionToPaths` mints one where none existed. */
  if (rules.length === 0) return null;

  return { ...omit(filter, 'filterId'), rules };
};
